import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Collapse } from 'react-bootstrap';

import logger from 'ws-scripts/modules/logger';
import { setNewRelicCustomAttribute } from '@ddc/new-relic';
import { useRequestData, usePrefs, useLabels, useFlags } from 'wsm-common-data';
import { ErrorBoundary } from 'wsm-error-boundary';
import { PricingDetail } from 'wsm-pricing-display';
import { useDealerOptions, useDealerFees } from 'wsm-pricing-data';

import vehiclePropType from '../../../propTypes/vehiclePropType';
import { pricingMap } from '../../../utilities/pricingMap';
import { sanitizeIncentiveTitles } from '../../../utilities/sanitizeLabelMarkup';
import {
	getTargetLocations,
	getVehicleLocationName
} from '../../../utilities/getTargetLocations';
import ConditionalWrapper from '../../ConditionalWrapper';
import { noticeError } from '../../../utilities/newRelic';
import {
	selectIsGridLayout,
	selectIsListLayout
} from '../../../utilities/layout';

const PricingElement = ({
	accountId,
	accountInfo,
	incentives,
	isDisplayingEPrice, // TODO: fix prop drilling down from DetailsContainer
	setShowExpandedPricing,
	showExpandedPricing,
	showNewCarBoostPaymentsInDetails,
	vehicle
}) => {
	const { restrictInsertLocationsForVehicles, showInformationTabs } =
		usePrefs();
	const labels = useLabels();
	const flags = useFlags();
	const { widgetName, deviceType, locale } = useRequestData();
	const isListLayout = useSelector((state) => selectIsListLayout(state));
	const isGridLayout = useSelector((state) => selectIsGridLayout(state));
	const { userProfile } = useSelector((state) => state);

	const shouldSanitizeLabels = Boolean(
		flags['ws-inv-listing-strip-invalid-pricing-elements']
	);

	const componentPricing = pricingMap(vehicle.pricing, shouldSanitizeLabels);

	const safeIncentives = useMemo(
		() =>
			shouldSanitizeLabels
				? sanitizeIncentiveTitles(incentives)
				: incentives,
		[incentives, shouldSanitizeLabels]
	);

	const [shouldRequestDealerOptions, setShouldRequestDealerOptions] =
		useState(false);

	const { dealerOptions, isLoadingDealerOptions } = useDealerOptions(
		vehicle?.uuid,
		locale,
		shouldRequestDealerOptions,
		widgetName
	);
	const handleDealerOptionsMouseEnter = () =>
		setShouldRequestDealerOptions(true);

	const [shouldRequestDealerFees, setShouldRequestDealerFees] =
		useState(false);

	const { dealerFees, isLoadingDealerFees } = useDealerFees(
		vehicle?.uuid,
		locale,
		shouldRequestDealerFees,
		widgetName
	);
	const handleDealerFeesMouseEnter = () => setShouldRequestDealerFees(true);

	const isApplyNewCarBoostForListView =
		showNewCarBoostPaymentsInDetails && isListLayout;

	// TODO: this can be refactored into a util
	const targetLocations = getTargetLocations(
		vehicle,
		restrictInsertLocationsForVehicles
	);

	const vehiclePaymentsLocationPrimary = getVehicleLocationName(
		'vehicle-payments-primary',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	const vehiclePaymentsLocation = getVehicleLocationName(
		'vehicle-payments',
		targetLocations,
		restrictInsertLocationsForVehicles
	);

	return (
		<ConditionalWrapper
			condition={
				!isListLayout &&
				!showInformationTabs?.toLowerCase().includes('pricing')
			}
			wrapper={(children) => (
				<Collapse in={showExpandedPricing}>
					<div
						className={[
							deviceType === 'MOBILE'
								? 'ddc-font-size-small'
								: 'ddc-font-size-xsmall',
							'mt-2'
						]}
					>
						{children}
						<button
							type="button"
							className="btn-link btn-no-decoration p-0 d-block text-muted flex-fill w-100 text-right mb-4"
							onClick={() => setShowExpandedPricing(false)}
						>
							{labels.get('HIDE_PRICING')}
						</button>
					</div>
				</Collapse>
			)}
		>
			<ErrorBoundary
				errorHandler={(error, errorInfo) => {
					setNewRelicCustomAttribute(
						'SRP ERROR',
						`ws-inv-listing error boundary: wsm-pricing-display error.\n${error}`
					);
					const newError = new Error(
						`ws-inv-listing error boundary.\n${error}`
					);
					newError.originalError = error;
					newError.originalStackTrace = errorInfo.componentStack;
					logger.error(`${newError}\n${newError.originalStackTrace}`);
					noticeError(error, {
						widget: 'wsm-pricing-display',
						boundary: 'ws-inv-listing',
						accountId,
						vehicleAccountId: vehicle?.accountId,
						vehicleId: vehicle?.uuid,
						componentStack: errorInfo?.componentStack?.slice(
							0,
							4000
						)
					});
				}}
			>
				{isApplyNewCarBoostForListView && (
					<div
						className="hidden vehicle-card-payments mt-3"
						data-location={vehiclePaymentsLocationPrimary}
					/>
				)}
				<PricingDetail
					pricing={componentPricing}
					incentives={safeIncentives}
					accountInfo={accountInfo}
					accountId={accountId}
					condition={vehicle?.condition}
					isGridLayout={isGridLayout}
					userProfile={userProfile}
					fetchDealerOptionsCallback={handleDealerOptionsMouseEnter}
					dealerOptions={dealerOptions}
					isLoadingDealerOptions={isLoadingDealerOptions}
					fetchDealerFeesCallback={handleDealerFeesMouseEnter}
					dealerFees={dealerFees}
					isLoadingDealerFees={isLoadingDealerFees}
					vehicleAccountId={vehicle?.accountId}
					vehicleId={vehicle?.uuid}
					isDisplayingEPrice={isDisplayingEPrice}
					vehicleData={{
						images: vehicle?.images,
						title: vehicle?.title
					}}
				/>
				{!isApplyNewCarBoostForListView && (
					<div
						className="hidden vehicle-card-payments mt-3"
						data-location={vehiclePaymentsLocation}
					/>
				)}
			</ErrorBoundary>
		</ConditionalWrapper>
	);
};

PricingElement.propTypes = {
	accountId: PropTypes.string.isRequired,
	accountInfo: PropTypes.shape({}),
	incentives: PropTypes.shape({}),
	isDisplayingEPrice: PropTypes.bool.isRequired,
	setShowExpandedPricing: PropTypes.func.isRequired,
	showExpandedPricing: PropTypes.bool.isRequired,
	showNewCarBoostPaymentsInDetails: PropTypes.bool,
	vehicle: vehiclePropType.isRequired
};

export default PricingElement;
